import React from 'react';
import { Box, Collapse, Radio, Stack } from '@mantine/core';
import FormDateInput, { fromIsoDate, isRealDate } from '@/components/form/Form.Date.Input';
import { DATE_PRESETS, DATE_SHORTCUTS } from '../filter.constants';
import type { TypeDateSection } from '@/types/list/types.list.filter';

type Props = {
    section: TypeDateSection;
    value: string;
    onChange: (value: string) => void;
};

const ANY = 'any';
const CUSTOM = 'custom';

const toDay = (masked: string): string => {
    if (!isRealDate(masked)) return '';
    const [day, month, year] = masked.split('/');
    return `${year}-${month}-${day}`;
};

const fromDay = (day: string): string => (day ? fromIsoDate(`${day}T12:00:00`) : '');

const GROUPS = [
    [{ value: ANY, label: 'Qualsiasi data' }],
    DATE_SHORTCUTS,
    DATE_PRESETS.map((preset) => ({ value: preset.value, label: `Ultimi ${preset.label}` })),
    [{ value: CUSTOM, label: 'Intervallo personalizzato' }],
];

const FilterDates: React.FC<Props> = ({ section, value, onChange }) => {
    const range = value.startsWith('range:') ? value.split(':') : null;
    const [from, setFrom] = React.useState(fromDay(range?.[1] ?? ''));
    const [to, setTo] = React.useState(fromDay(range?.[2] ?? ''));
    const [custom, setCustom] = React.useState(Boolean(range));

    const chosen = custom ? CUSTOM : value || ANY;

    const commit = (nextFrom: string, nextTo: string) => {
        const start = toDay(nextFrom);
        const end = toDay(nextTo);
        const incomplete = (nextFrom && !start) || (nextTo && !end);
        if (incomplete || (start && end && start > end)) return;
        if (!start && !end) {
            if (value.startsWith('range:')) onChange('');
            return;
        }
        onChange(`range:${start}:${end}`);
    };

    const pick = (next: string) => {
        if (next === CUSTOM) {
            setCustom(true);
            commit(from, to);
            return;
        }
        setCustom(false);
        setFrom('');
        setTo('');
        onChange(next === ANY ? '' : next);
    };

    const reversed = Boolean(toDay(from) && toDay(to) && toDay(from) > toDay(to));

    return (
        <Box className="bd-fp-section" role="group" aria-label={section.label}>
            <Radio.Group value={chosen} onChange={pick} aria-label={section.label}>
                <Stack gap={0}>
                    {GROUPS.map((group, index) => (
                        <Box key={group[0].value} className="bd-fp-radio-group" data-first={index === 0 || undefined}>
                            {group.map((option) => (
                                <Box component="label" key={option.value} className="bd-fp-option" data-checked={option.value === chosen || undefined}>
                                    <Radio size="xs" value={option.value} aria-label={option.label} />
                                    <span className="bd-fp-option-label">{option.label}</span>
                                </Box>
                            ))}
                        </Box>
                    ))}
                </Stack>
            </Radio.Group>

            <Collapse expanded={custom} transitionDuration={150}>
                <Box className="bd-fp-range">
                    <FormDateInput
                        size="sm"
                        label="Dal"
                        value={from}
                        onChange={(next) => { setFrom(next); commit(next, to); }}
                        error={from && from.length === 10 && !isRealDate(from) ? 'Data inesistente' : undefined}
                    />
                    <FormDateInput
                        size="sm"
                        label="Al"
                        value={to}
                        onChange={(next) => { setTo(next); commit(from, next); }}
                        error={to && to.length === 10 && !isRealDate(to) ? 'Data inesistente' : reversed ? 'Viene prima di “Dal”' : undefined}
                    />
                </Box>
            </Collapse>
        </Box>
    );
};

export default FilterDates;
